// Section. Back-of-garment cross-sell (2026-07-02 CRO audit, item 2.3).
//
// The back placement was previously only reachable via the unprompted
// "+ Add another spot" button. This card prompts it the moment the
// customer has their first spot saved: "same design, bigger, on the
// back", anchored at the real minimum extra cost (additional-placement
// ladder + the cheapest back size tier). One tap spawns a pre-filled
// draft that clones the saved design onto the back placement at a big
// default size, marked already-digitised so pricing never re-charges
// the setup (mirrors pickReuse in App.tsx).
//
// Renders ONLY when:
//   - The customer has at least one saved spot and no editor is open
//   - The garment has a back (garmentType.hasBack !== false)
//   - The template offers a back placement that isn't excluded by an
//     existing spot and no spot already sits on it
//   - The back canvas is big enough to sell as "big" (>= 150mm wide)

import type { Config, Placement } from "../types";
import type { EmbroideryDraft } from "./PlacementSection";
import type { Spot } from "../pricing";
import { additionalPlacementFeeFor, effectiveTiersFor } from "../pricing";
import { flowKindFor } from "../flow";

// Placement slugs that count as "on the back". Matches the canonical
// tiered slugs on this shop plus underscore variants; comparison is
// case-insensitive via lowercasing at lookup-time.
const BACK_SLUGS = new Set([
  "cb",
  "centre-back",
  "center-back",
  "back",
  "full-back",
  "upper-back",
  "centre_back",
  "center_back",
  "full_back",
  "upper_back",
]);

function isBackSlug(slug: string | null | undefined): boolean {
  return !!slug && BACK_SLUGS.has(slug.toLowerCase());
}

// The back canvas has to be genuinely big for "big back design" copy to
// make sense. Caps / beanies expose narrow back placements (~60mm) that
// this card should never prompt for.
const MIN_BIG_BACK_MM = 150;

export function BackCrossSellSection({
  config,
  spots,
  draft,
  lockedSlugs,
  onPickBack,
}: {
  config: Config;
  spots: Spot[];
  draft: EmbroideryDraft | null;
  /** Placement slugs excluded by existing spots (slug -> reason). The
   *  card hides when the back placement is locked so we never prompt a
   *  spot the editor would refuse. */
  lockedSlugs: Map<string, string>;
  /** Spawns the pre-filled draft. Parent (App.tsx) sets it as the
   *  active draft; this section unmounts once the spot saves onto the
   *  back placement. */
  onPickBack: (next: EmbroideryDraft) => void;
}) {
  const flow = flowKindFor(config);

  const garmentHasBack =
    (config.garmentType as { hasBack?: boolean }).hasBack !== false;

  const backPlacement: Placement | undefined = config.placements.find((p) =>
    isBackSlug(p.slug),
  );

  const haveBackSpot = spots.some((s) => isBackSlug(s.placementSlug));

  if (!garmentHasBack) return null;
  if (!backPlacement) return null;
  if (spots.length === 0) return null;
  if (haveBackSpot) return null;
  if (draft !== null) return null;
  if (lockedSlugs.has(backPlacement.slug)) return null;

  const cap = Number.isFinite(backPlacement.maxWidthMm)
    ? backPlacement.maxWidthMm
    : null;
  if (cap !== null && cap < MIN_BIG_BACK_MM) return null;

  // The design to reuse: prefer the first spot carrying an upload (the
  // pet portrait / kid drawing / logo), else the first saved spot (a
  // text design blows up on the back just as well).
  const sourceSpot =
    spots.find(
      (s) => (s.mode === "logo" || s.mode === "both") && !!s.uploadStagingKey,
    ) ?? spots[0];

  // Honest floor price for the card: the additional-placement fee this
  // spot would pay (pricing.ts ladder, first spot free) plus the
  // cheapest back size tier. Reusing the saved design means no new
  // digitisation, so ladder + tier IS the whole extra cost.
  const nth = spots.length + 1;
  const ladderCents = additionalPlacementFeeFor(config, nth);
  const tiers = effectiveTiersFor(backPlacement, config);
  const minTierCents =
    tiers.length > 0 ? Math.min(...tiers.map((t) => t.priceCents)) : 0;
  const fromCents = ladderCents + minTierCents;

  // Pre-fill the draft at the biggest size the back allows (top visible
  // tier is already clipped to the placement cap by effectiveTiersFor),
  // so the editor opens on the "big" configuration the copy promises.
  const defaultSizeMm =
    tiers.length > 0
      ? tiers[tiers.length - 1].maxMm
      : Math.min(backPlacement.defaultWidthMm || 200, cap ?? 200);

  const sourceFont = sourceSpot.fontId
    ? config.fonts.find((f) => f.id === sourceSpot.fontId)
    : null;
  const sourceHasUpload =
    (sourceSpot.mode === "logo" || sourceSpot.mode === "both") &&
    !!sourceSpot.uploadStagingKey;
  const sourceText =
    (sourceSpot.mode === "text" || sourceSpot.mode === "both") &&
    sourceSpot.text.trim()
      ? sourceFont?.forceUppercase
        ? sourceSpot.text.trim().toUpperCase()
        : sourceSpot.text.trim()
      : null;

  const designLabel = sourceHasUpload
    ? flow === "pet"
      ? "Your pet's portrait"
      : flow === "kid"
        ? "Their drawing"
        : sourceSpot.uploadFilename || "Your uploaded design"
    : sourceText ?? "Your design";

  const title =
    flow === "pet"
      ? "Put them big on the back"
      : flow === "kid"
        ? "Their drawing, big on the back"
        : "Add a big back design";
  const summaryLine =
    flow === "pet" || flow === "kid"
      ? "Same design, bigger. It's already digitised, so there's no extra setup."
      : "Same design, bigger, stitched across the back. Setup's already done.";

  const handleAdd = () => {
    // Clone the saved design onto the back placement at the big default
    // size. uploadAlreadyDigitised mirrors pickReuse: the artwork was
    // digitised (and paid for) on the front spot, so pricing must not
    // charge the fee / scan upsell / per-photo counts again. Sleeve
    // emoji extras stay behind on the sleeve spot.
    const { id: _id, ...src } = sourceSpot;
    onPickBack({
      ...src,
      placementSlug: backPlacement.slug,
      sizeMm: defaultSizeMm,
      uploadAlreadyDigitised: sourceHasUpload ? true : src.uploadAlreadyDigitised,
      extraAddonIds: [],
    });
  };

  return (
    <section className="rk-pc-sec is-active rk-pc-back-cross-sell">
      <div className="rk-pc-sec__head" role="heading" aria-level={2}>
        <span className="rk-pc-sec__num">+</span>
        <span className="rk-pc-sec__heading">
          <span className="rk-pc-sec__title">{title}</span>
          <span className="rk-pc-sec__summary">{summaryLine}</span>
        </span>
        <span className="rk-pc-sec__price">
          from +${(fromCents / 100).toFixed(0)}
        </span>
      </div>
      <div className="rk-pc-sec__body">
        <div className="rk-pc-back-reuse">
          <span className="rk-pc-back-reuse__glyph" aria-hidden>
            ▣
          </span>
          <span
            className="rk-pc-back-reuse__label"
            style={
              !sourceHasUpload && sourceFont
                ? {
                    fontFamily: `'${sourceFont.family}', system-ui, sans-serif`,
                    letterSpacing: sourceFont.tracking ?? "0",
                  }
                : undefined
            }
          >
            {designLabel}
          </span>
          <span className="rk-pc-back-reuse__arrow" aria-hidden>
            →
          </span>
          <span className="rk-pc-back-reuse__target">
            {backPlacement.name}, up to {defaultSizeMm}mm wide
          </span>
        </div>
        <button
          type="button"
          className="rk-pc-sleeve-cta rk-pc-back-cta"
          onClick={handleAdd}
        >
          Add it to the back, from +${(fromCents / 100).toFixed(0)}
        </button>
      </div>
    </section>
  );
}
